import { AsYouType, CountryCode, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { E164_MAX_DIGITS } from './phone-format';

export interface PhoneCountry {
  iso2: CountryCode;
  name: string;
  dialCode: string;
  /** Quantidade máxima de dígitos do número nacional (sem DDI) para este país. */
  digitCap: number;
}

function createRegionNames(): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames(['pt'], { type: 'region' });
  } catch {
    return null;
  }
}

const REGION_NAMES = createRegionNames();

function countryName(iso2: string): string {
  try {
    return (REGION_NAMES && REGION_NAMES.of(iso2)) || iso2;
  } catch {
    return iso2;
  }
}

function nationalDigitCap(dialCodeLength: number): number {
  return Math.max(E164_MAX_DIGITS - dialCodeLength, 1);
}

/**
 * Formata um número (parcial ou completo) do jeito usado pela PhoneDirective:
 * tenta o AsYouType e, se ele não aplicar nenhuma máscara mas o número já for
 * válido, recorre ao formatNational() oficial (cobre países cujo template só
 * existe com o prefixo de tronco, ex.: "0", que não faz parte do E.164).
 */
export function formatLikeDirective(iso2: CountryCode, nationalValue: string): string {
  const formatted = new AsYouType(iso2).input(nationalValue);
  if (formatted !== nationalValue) {
    return formatted;
  }
  const parsed = parsePhoneNumberFromString(nationalValue, iso2);
  return parsed ? parsed.formatNational() : formatted;
}

// Brasil sempre em primeiro, os demais em ordem alfabética (pt-BR)
export const PHONE_COUNTRIES: PhoneCountry[] = getCountries()
  .map(iso2 => {
    const dialCode = getCountryCallingCode(iso2);
    const digitCap = nationalDigitCap(dialCode.length);

    return {
      iso2,
      name: countryName(iso2),
      dialCode,
      digitCap
    };
  })
  .sort((a, b) => {
    if (a.iso2 === 'BR') return -1;
    if (b.iso2 === 'BR') return 1;
    return a.name.localeCompare(b.name, 'pt-BR');
  });

export const DEFAULT_PHONE_COUNTRY: CountryCode = 'BR';

const PHONE_COUNTRY_BY_ISO2 = new Map(PHONE_COUNTRIES.map(country => [country.iso2, country]));

export function getPhoneCountry(iso2: CountryCode): PhoneCountry {
  return PHONE_COUNTRY_BY_ISO2.get(iso2) || PHONE_COUNTRY_BY_ISO2.get(DEFAULT_PHONE_COUNTRY)!;
}
